import { useCallback, useMemo, useState } from 'react'
import { Trash2, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import {
  EditableGrid,
  cellKey,
  type EditableColumnDef,
} from '@/components/ui/EditableGrid/EditableGrid'
import { ProductPicker } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { ApiError } from '@/lib/api/jsonFetch'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useCreateVendorItem,
  useDeleteVendorItem,
  usePatchVendorItem,
} from '../api/queries'
import type { VendorItem, VendorItemPatch, VendorRow } from '../api/types'

/**
 * Vendor side of the editor — mirrors the structure of
 * `customer-pricelists/components/PricelistEditor.tsx`:
 *
 * - Existing rows show the product as a read-only Odoo link; only draft
 *   rows can pick a product (the supplierinfo line is keyed on
 *   partner_id + product_tmpl_id; changing the product after creation
 *   would silently break PO history)
 * - Other cells edit inline. Edits POST immediately via PATCH; an
 *   optimistic patch lights the cell while the request is in flight.
 *   400s from Odoo route through `cellErrors`; other errors fall back
 *   to a toast.
 * - Delete via per-row icon. Add via the bottom "Add product" link.
 *
 * Sample products are filtered server-side so they never appear here.
 */

interface DraftRow {
  /** Synthetic negative id — never hits Odoo. */
  id: number
  _draft: true
  vendor_id: number
  vendor_name: string | null
  product_tmpl_id: number | null
  product_name: string | null
  vendor_product_code: string | null
  vendor_product_name: string | null
  min_qty: number
  price: number | null
  lead_time_days: number
  incoterm: string | null
  date_start: string | null
  date_end: string | null
  discount: number
  pack_size: number
  // Read-only display fields, kept to match the union shape
  currency_id: number | null
  currency_name: string | null
  notes: string | null
  write_date: string | null
  sequence: number
  product_uom_name: string | null
  product_variant_id: number | null
  product_variant_name: string | null
  agreement_id: number | null
  agreement_name: string | null
}

type GridRow = (VendorItem & { _draft?: false }) | DraftRow

function isDraft(row: GridRow): row is DraftRow {
  return (row as DraftRow)._draft === true
}

export interface VendorPricelistEditorProps {
  vendor: VendorRow
  items: VendorItem[]
  refetch: () => void
  fetching: boolean
}

export function VendorPricelistEditor({
  vendor,
  items,
  refetch,
  fetching,
}: VendorPricelistEditorProps) {
  const patchItem = usePatchVendorItem()
  const createItem = useCreateVendorItem()
  const deleteItem = useDeleteVendorItem()

  const [draftRows, setDraftRows] = useState<DraftRow[]>([])
  const [pendingEdits, setPendingEdits] = useState<
    Map<number, Partial<VendorItem>>
  >(new Map())
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map())

  // Server rows with pending optimistic edits layered, then drafts.
  const rows: GridRow[] = useMemo(() => {
    const merged: GridRow[] = items.map((it) => ({
      ...it,
      ...(pendingEdits.get(it.id) ?? {}),
    }))
    return [...merged, ...draftRows]
  }, [items, pendingEdits, draftRows])

  const setCellError = useCallback((key: string, msg: string | null) => {
    setCellErrors((prev) => {
      const next = new Map(prev)
      if (msg === null) {
        next.delete(key)
      } else {
        next.set(key, msg)
      }
      return next
    })
  }, [])

  // ─── column defs ─────────────────────────────────────────────────────

  const columns: EditableColumnDef<GridRow>[] = useMemo(
    () => [
      {
        key: 'product_name',
        header: 'Product',
        width: '24%',
        readonly: (row) => !isDraft(row),
        render: (_value, row) => {
          if (isDraft(row)) {
            if (!row.product_tmpl_id) {
              return <span className="text-tertiary">Pick product…</span>
            }
            return <span className="truncate">{row.product_name ?? '—'}</span>
          }
          const odooHref = odooUrl('product.template', row.product_tmpl_id)
          const label = row.product_name ?? '—'
          return (
            <a
              href={odooHref}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${label} in Odoo`}
              title="Open in Odoo"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 truncate text-link hover:underline"
            >
              <span className="truncate">{label}</span>
              <ExternalLink size={10} aria-hidden="true" className="flex-shrink-0" />
            </a>
          )
        },
        renderEditor: ({ row, onCommit, onCancel }) => {
          if (!isDraft(row)) {
            onCancel()
            return null
          }
          const pickerValue: PickerValue = row.product_tmpl_id
            ? [row.product_tmpl_id, row.product_name ?? '']
            : null
          return (
            <ProductPicker
              value={pickerValue}
              onChange={(next) => onCommit(next)}
              editorMode
              onCancel={onCancel}
              excludeSamples
            />
          )
        },
      },
      {
        key: 'vendor_product_code',
        header: 'Vendor SKU',
        width: '12%',
        render: (v) =>
          v ? <span>{v as string}</span> : <span className="text-tertiary">—</span>,
      },
      {
        key: 'vendor_product_name',
        header: 'Vendor product name',
        width: '14%',
        render: (v) =>
          v ? <span>{v as string}</span> : <span className="text-tertiary">—</span>,
      },
      // Read-only display parity with Odoo's native "Vendor Pricelists"
      // list (action=169) — nobody at SPI uses Product Variant today;
      // no editor is wired, so the cell is implicitly non-editable.
      {
        key: 'product_variant_name',
        header: 'Product variant',
        width: '9%',
        render: (_value, row) => {
          const label = (row as VendorItem).product_variant_name
          const id = (row as VendorItem).product_variant_id
          if (!label || !id) return <span className="text-tertiary">—</span>
          return (
            <a
              href={odooUrl('product.product', id)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 truncate text-link hover:underline"
            >
              <span className="truncate">{label}</span>
              <ExternalLink size={10} aria-hidden="true" className="flex-shrink-0" />
            </a>
          )
        },
      },
      {
        key: 'min_qty',
        header: 'Min qty',
        numeric: true,
        width: '7%',
        render: (v) => formatQty(v as number | null),
      },
      // Read-only — set at the product template's purchase-UOM level,
      // not per supplierinfo row (2026-08-11 decision with Chris).
      {
        key: 'product_uom_name',
        header: 'Unit of measure',
        width: '7%',
        render: (v) =>
          v ? <span>{v as string}</span> : <span className="text-tertiary">—</span>,
      },
      {
        key: 'pack_size',
        header: 'Pack size',
        numeric: true,
        width: '7%',
        render: (v) => formatQty(v as number | null),
      },
      {
        key: 'price',
        header: 'Price',
        numeric: true,
        width: '10%',
        render: (v, row) => formatPrice(v as number | null, row.currency_name),
      },
      {
        key: 'discount',
        header: 'Discount (%)',
        numeric: true,
        width: '7%',
        render: (v) => {
          const n = v as number | null
          return n === null || n === undefined ? '—' : `${n.toLocaleString()}%`
        },
      },
      {
        key: 'lead_time_days',
        header: 'Lead (days)',
        numeric: true,
        width: '6%',
        render: (v) => {
          const n = v as number | null
          return n === null || n === undefined ? '—' : n.toLocaleString()
        },
      },
      {
        key: 'incoterm',
        header: 'Incoterm',
        width: '6%',
        render: (v) =>
          v ? <span>{v as string}</span> : <span className="text-tertiary">—</span>,
      },
      // Read-only — Agreement is a link to the separate Purchase
      // Agreements workflow; nothing at SPI uses it today, and it
      // shouldn't be hand-picked from a pricing grid without that
      // workflow behind it (2026-08-11 decision with Chris).
      {
        key: 'agreement_name',
        header: 'Agreement',
        width: '8%',
        render: (_value, row) => {
          const label = (row as VendorItem).agreement_name
          const id = (row as VendorItem).agreement_id
          if (!label || !id) return <span className="text-tertiary">—</span>
          return (
            <a
              href={odooUrl('purchase.requisition', id)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 truncate text-link hover:underline"
            >
              <span className="truncate">{label}</span>
              <ExternalLink size={10} aria-hidden="true" className="flex-shrink-0" />
            </a>
          )
        },
      },
      {
        key: 'date_start',
        header: 'Valid from',
        width: '7%',
        render: (v) => (v as string | null) || <span className="text-tertiary">—</span>,
      },
      {
        key: 'date_end',
        header: 'Valid to',
        width: '7%',
        render: (v) => (v as string | null) || <span className="text-tertiary">—</span>,
      },
      // Read-only display — no currency picker exists anywhere in the
      // app yet; same reasoning as Unit of Measure above.
      {
        key: 'currency_name',
        header: 'Currency',
        width: '6%',
        render: (v) =>
          v ? <span>{v as string}</span> : <span className="text-tertiary">—</span>,
      },
      {
        key: 'notes',
        header: 'Notes',
        width: '10%',
        render: (v) =>
          v ? <span className="truncate">{v as string}</span> : <span className="text-tertiary">—</span>,
      },
    ],
    [],
  )

  // ─── commit a draft (POST) ───────────────────────────────────────────
  //
  // Declared BEFORE `handleCellChange` so the lint rule
  // react-hooks/immutability sees it as already-defined. Reordered
  // 2026-05-28 to fix CI lint.

  const commitDraft = useCallback(
    (draft: DraftRow, rowIndex: number) => {
      if (draft.product_tmpl_id === null || draft.price === null) return
      const body = {
        product_tmpl_id: draft.product_tmpl_id,
        price: draft.price,
        min_qty: draft.min_qty,
        lead_time: draft.lead_time_days,
        vendor_product_code: draft.vendor_product_code,
        vendor_product_name: draft.vendor_product_name,
        incoterm: draft.incoterm,
        notes: draft.notes,
        discount: draft.discount,
        pack_size: draft.pack_size,
      }
      createItem.mutate(
        { vendorId: vendor.id, item: body },
        {
          onSuccess: () => {
            setDraftRows((prev) => prev.filter((d) => d.id !== draft.id))
            refetch()
          },
          onError: (err) => {
            if (err instanceof ApiError && err.status === 400) {
              const ci = columns.findIndex((c) => c.key === 'price')
              setCellError(cellKey(rowIndex, ci), err.message || 'Validation failed')
            } else {
              toast.error(err instanceof Error ? err.message : 'Add failed')
            }
          },
        },
      )
    },
    [vendor.id, createItem, columns, refetch, setCellError],
  )

  // ─── commit a cell change ────────────────────────────────────────────

  const handleCellChange = useCallback(
    (rowIndex: number, key: keyof GridRow, value: unknown) => {
      const row = rows[rowIndex]
      if (!row) return

      // Coerce per column.
      let coerced: unknown = value
      if (key === 'price' || key === 'min_qty' || key === 'discount' || key === 'pack_size') {
        const n = typeof value === 'string' ? Number(value) : value
        if (typeof n !== 'number' || Number.isNaN(n) || n < 0) {
          setCellError(cellKey(rowIndex, columns.findIndex((c) => c.key === key)),
            `${String(key)} must be a non-negative number`)
          return
        }
        coerced = n
      } else if (key === 'lead_time_days') {
        const n = typeof value === 'string' ? Number(value) : value
        if (typeof n !== 'number' || Number.isNaN(n) || n < 0 || !Number.isInteger(n)) {
          setCellError(cellKey(rowIndex, columns.findIndex((c) => c.key === key)),
            'Lead time must be a non-negative integer')
          return
        }
        coerced = n
      } else if (key === 'product_name' && isDraft(row)) {
        // Product picker on a draft row — value is the PickerValue tuple
        const picked = value as PickerValue | null
        if (!picked) return
        setDraftRows((prev) =>
          prev.map((d) =>
            d.id === row.id
              ? { ...d, product_tmpl_id: picked[0], product_name: picked[1] }
              : d,
          ),
        )
        // If draft has product + price now, try to POST it.
        const updatedDraft = { ...row, product_tmpl_id: picked[0], product_name: picked[1] }
        if (updatedDraft.product_tmpl_id !== null && updatedDraft.price !== null) {
          commitDraft(updatedDraft, rowIndex)
        }
        return
      } else if (typeof value === 'string') {
        coerced = value.trim() === '' ? null : value.trim()
      }

      // Clear any previous cell error on this cell.
      const ci = columns.findIndex((c) => c.key === key)
      setCellError(cellKey(rowIndex, ci), null)

      // Draft row: write into the draft, and POST if the minimum
      // required fields are now present.
      if (isDraft(row)) {
        setDraftRows((prev) =>
          prev.map((d) =>
            d.id === row.id ? ({ ...d, [key]: coerced } as DraftRow) : d,
          ),
        )
        const updatedDraft = { ...row, [key]: coerced } as DraftRow
        if (updatedDraft.product_tmpl_id !== null && updatedDraft.price !== null) {
          commitDraft(updatedDraft, rowIndex)
        }
        return
      }

      // Existing row: optimistic patch + PATCH request.
      const existing = row as VendorItem
      setPendingEdits((prev) => {
        const next = new Map(prev)
        const current = next.get(existing.id) ?? {}
        next.set(existing.id, { ...current, [key]: coerced as never })
        return next
      })

      const body: VendorItemPatch = {}
      if (key === 'lead_time_days') {
        body.lead_time = coerced as number
      } else {
        ;(body as Record<string, unknown>)[key as string] = coerced
      }

      patchItem.mutate(
        { itemId: existing.id, vendorId: vendor.id, patch: body },
        {
          onSuccess: () => {
            setPendingEdits((prev) => {
              const next = new Map(prev)
              const current = next.get(existing.id)
              if (!current) return prev
              const updated = { ...current }
              delete (updated as Record<string, unknown>)[key as string]
              if (Object.keys(updated).length === 0) {
                next.delete(existing.id)
              } else {
                next.set(existing.id, updated)
              }
              return next
            })
            refetch()
          },
          onError: (err) => {
            setPendingEdits((prev) => {
              const next = new Map(prev)
              const current = next.get(existing.id)
              if (!current) return prev
              const updated = { ...current }
              delete (updated as Record<string, unknown>)[key as string]
              if (Object.keys(updated).length === 0) {
                next.delete(existing.id)
              } else {
                next.set(existing.id, updated)
              }
              return next
            })
            if (err instanceof ApiError && err.status === 400) {
              setCellError(cellKey(rowIndex, ci), err.message || 'Validation failed')
            } else {
              toast.error(err instanceof Error ? err.message : 'Update failed')
            }
          },
        },
      )
    },
    [rows, columns, vendor.id, patchItem, refetch, setCellError, commitDraft],
  )

  // ─── add / delete ────────────────────────────────────────────────────

  function handleAddRow() {
    const id = -1 * (draftRows.length + 1)
    setDraftRows((prev) => [
      ...prev,
      {
        id,
        _draft: true,
        vendor_id: vendor.id,
        vendor_name: vendor.name,
        product_tmpl_id: null,
        product_name: null,
        vendor_product_code: null,
        vendor_product_name: null,
        min_qty: 1,
        price: null,
        lead_time_days: 0,
        incoterm: null,
        date_start: null,
        date_end: null,
        discount: 0,
        pack_size: 0,
        currency_id: null,
        currency_name: null,
        notes: null,
        write_date: null,
        sequence: 10,
        product_uom_name: null,
        product_variant_id: null,
        product_variant_name: null,
        agreement_id: null,
        agreement_name: null,
      },
    ])
  }

  function handleDelete(row: GridRow) {
    if (isDraft(row)) {
      setDraftRows((prev) => prev.filter((d) => d.id !== row.id))
      return
    }
    if (!window.confirm(`Remove "${row.product_name ?? 'this item'}"?`)) return
    deleteItem.mutate(
      { itemId: row.id, vendorId: vendor.id },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Delete failed')
        },
        onSuccess: () => {
          refetch()
        },
      },
    )
  }

  // ─── dirty cells ─────────────────────────────────────────────────────

  const dirtyCells = useMemo(() => {
    const out = new Set<string>()
    items.forEach((it, ri) => {
      const patch = pendingEdits.get(it.id)
      if (!patch) return
      columns.forEach((col, ci) => {
        if ((patch as Partial<VendorItem>)[col.key as keyof VendorItem] !== undefined) {
          out.add(cellKey(ri, ci))
        }
      })
    })
    return out
  }, [items, pendingEdits, columns])

  return (
    <div className="flex flex-col gap-2">
      <EditableGrid
        rows={rows}
        columns={columns}
        moduleId="vendor-pricelists"
        onCellChange={handleCellChange}
        onAddRow={handleAddRow}
        addRowLabel="Add item"
        dirtyCells={dirtyCells}
        cellErrors={cellErrors}
        rowActions={(row) => (
          <button
            type="button"
            aria-label="Remove row"
            onClick={() => handleDelete(row)}
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-danger"
          >
            <Trash2 size={12} />
          </button>
        )}
      />
      <footer className="px-2 text-2xs text-tertiary">
        Tab | Enter | Esc | ↑↓ | Edits save immediately. Click a header to sort; drag a header to reorder.
        {fetching ? <span className="ml-2">· Refreshing…</span> : null}
      </footer>
    </div>
  )
}

function formatQty(q: number | null | undefined): string {
  if (q === null || q === undefined) return '—'
  return q.toLocaleString()
}

function formatPrice(p: number | null | undefined, currency: string | null): string {
  if (p === null || p === undefined) return '—'
  const ccy = currency ?? 'USD'
  return `${ccy} ${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
}
